import { useRegistrationFormSaveEventRegistrationMutation, useEventSessionGetGuestKeySessionLazyQuery } from '@graphql/generated';
import { EventFormValues } from '../../../presentational/hooks/useEventFormState';
import { formStateToAnswers } from '../../shared/answersToFormState';
import { formStateToContactInfo } from '../../shared/formStateToContactInfo';
import { useToast } from '@withjoy/joykit';
import { useTranslation, getCookieStorage } from '@shared/core';
import { useRef } from 'react';

const useSendRegistration = () => {
  const [mutation] = useRegistrationFormSaveEventRegistrationMutation();

  const sendRegistration = async (data: EventFormValues & { formId: string; guestKey: string | undefined; eventId: string }) => {
    const { formValues, formId, guestKey, eventId } = data;
    const attendance = data.attendanceValue;

    const { data: response } = await mutation({
      variables: {
        input: {
          formId,
          guestKey,
          // attendance is null if there's no attendance question.
          // BUT, we still want to mark guests as attending, because they filled out the form.
          answers: formStateToAnswers(formValues, attendance ?? true),
          contactInfo: formStateToContactInfo(data, eventId),
        },
      },
    });

    return response;
  };

  return { sendRegistration };
};

export const useSaveRegistration = () => {
  const { sendRegistration } = useSendRegistration();
  const { toast } = useToast();
  const { tq } = useTranslation('eventFormDisplay');
  const cookieStorage = getCookieStorage();
  const sessionPromiseRef = useRef<{ resolve: () => void; reject: (error: Error) => void } | null>(null);

  const [getSession, { loading: isGetSessionLoading }] = useEventSessionGetGuestKeySessionLazyQuery({
    batchMode: 'fast',
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      if (cookieStorage && data.getRecipientSessionByGuestKey) {
        const sessionId = data.getRecipientSessionByGuestKey;
        cookieStorage.setItem('_session_id', sessionId);
      }
      sessionPromiseRef.current?.resolve();
    },
    onError: error => {
      sessionPromiseRef.current?.reject(error);
    },
  });

  const saveRegistration = async (data: EventFormValues & { formId: string; guestKey: string | undefined; eventId: string }) => {
    try {
      const response = await sendRegistration(data);
      const guestKey = response?.saveEventRegistration.guestKey;

      if (guestKey) {
        try {
          // this is needed because we have to wait the session is propertly created before redirect
          // so we wrap this in a Promise because lazy query execute functions does not return a Promise
          await new Promise<void>((resolve, reject) => {
            sessionPromiseRef.current = { resolve, reject };
            getSession({ variables: { guestKey, eventId: data.eventId } });

            // reject the Promise in case the getSession hangs
            setTimeout(() => {
              if (sessionPromiseRef.current) {
                sessionPromiseRef.current = null;
                reject(new Error('Session fetch timeout'));
              }
            }, 3000);
          });
        } catch (error) {
          // we do not want to block the user, add telemetry here
        }
      }
    } catch (error) {
      toast(tq`Error saving registration. Please try again later.`);
    }
  };

  return { saveRegistration, isGetSessionLoading };
};
